import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ApexChart, Button, ButtonGroup, Grid, Stack, Typography } from '@esgian/esgianui';
import { checkUserHasAccess, formatAxisValue } from '@helpers';
import { useSelector } from 'react-redux';
import { getUser } from '@store/features';
import { useTheme } from '@hooks';

const getMainChartDefOptions = (
  seriesMain,
  user,
  dateList,
  theme,
  idMain,
  groupName,
  fileName,
  type,
  yAxisTitle
) => {
  const {
    mode,
    palette: {
      charts: { background, rowColors, sevenColorsIndication }
    },
    typography: { fontFamily }
  } = theme;
  return {
    series: seriesMain,
    chart: {
      zoom: {
        enabled: true
      },
      id: idMain,
      type: type,
      // group: groupName,
      background: background,
      fontFamily: fontFamily,
      height: 350,
      toolbar: {
        offsetX: 0,
        offsetY: -30,
        tools: {
          download: checkUserHasAccess(user, true),
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        },
        export: {
          csv: {
            filename: fileName
          },
          svg: {
            filename: fileName
          },
          png: {
            filename: fileName
          }
        }
      }
    },
    colors: sevenColorsIndication,
    tooltip: {
      shared: true,
      intersect: false
    },
    dataLabels: {
      enabled: false
    },
    fill: {
      opacity: 1
    },
    markers: {
      size: 3
    },
    theme: {
      mode: mode
    },
    grid: {
      row: {
        colors: rowColors, // takes an array which will be repeated on columns
        opacity: 0.5
      }
    },
    stroke: {
      colors: type === 'bar' ? ['transparent'] : sevenColorsIndication,
      width: 2
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 0,
        columnWidth: '90%',
        barHeight: '100%'
      }
    },
    xaxis: {
      labels: { show: true, rotate: -45, rotateAlways: false, hideOverlappingLabels: false },
      categories: dateList
    },
    yaxis: {
      title: {
        text: yAxisTitle || ''
      },
      tickAmount: 5,
      max: (max) => max * 1.2,
      min: 0,
      labels: {
        minWidth: 50,
        maxWidth: 50,
        formatter: (value) => {
          if (value <= 5 && value !== 0) {
            return value?.toFixed(1);
          }
          return formatAxisValue(value);
        }
      }
    },
    legend: {
      showForSingleSeries: true,
      position: 'top',
      horizontalAlign: 'left'
    }
  };
};

const getBrushChartOptions = (
  series,
  dateList,
  theme,
  idBrush,
  groupName,
  type,
  user,
  fileName
) => {
  const {
    mode,
    typography: { fontFamily },
    palette: {
      charts: { background, rowColors }
    }
  } = theme;
  return {
    series: series,
    chart: {
      id: idBrush,
      type: type === 'bar' ? 'bar' : 'area',
      group: groupName,
      fontFamily: fontFamily,
      background: background,
      height: 130,
      toolbar: {
        tools: {
          download: checkUserHasAccess(user, true)
        },
        export: {
          csv: {
            filename: fileName
          },
          svg: {
            filename: fileName
          },
          png: {
            filename: fileName
          }
        }
      }
    },
    colors: ['#80DFEB', '#DD53FF', '#FFD465', '#0E9EFF', '#FFDAF9', '#4BAC3C', '#A47AFF'],
    stroke: {
      colors: ['#80DFEB', '#DD53FF', '#FFD465', '#0E9EFF', '#FFDAF9', '#4BAC3C', '#A47AFF'],
      width: 2
    },
    tooltip: { shared: true, intersect: false },

    dataLabels: {
      enabled: false
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 0,
        columnWidth: '75%',
        barHeight: '100%'
      }
    },
    fill: {
      opacity: type === 'bar' ? 1 : '0.4'
    },
    markers: {
      size: 3
    },
    theme: {
      mode: mode
    },
    xaxis: {
      labels: {
        rotate: -15,
        rotateAlways: false,
        hideOverlappingLabels: false
      },
      categories: dateList
    },
    grid: {
      row: {
        colors: rowColors,
        opacity: 0.5
      }
    },
    legend: {
      showForSingleSeries: false,
      position: 'top',
      horizontalAlign: 'left'
    },
    yaxis: {
      tickAmount: 3,
      title: {
        text: 'Total'
      },
      labels: {
        minWidth: 50,
        maxWidth: 50,
        formatter: (value) => {
          if (value <= 3 && value !== 0) {
            return value?.toFixed(1);
          }
          return formatAxisValue(value);
        }
      }
    }
  };
};

function TimeSeriesBrushChart({
  idMain,
  idBrush,
  groupName,
  fileName,
  dateList,
  seriesMain,
  seriesBrush,
  yAxisTitle,
  loading,
  isGrouped
}) {
  const [type, setType] = useState('bar');
  const [group, setGroup] = useState('monthly');
  const user = useSelector(getUser);
  const { theme } = useTheme();

  const { processedSeriesMain, processedSeriesBrush, processedDateList } = useMemo(() => {
    if (!seriesBrush[0]?.data) {
      return { processedSeriesMain: [], processedSeriesBrush: [], processedDateList: [] };
    }

    const expectedLength = dateList.length;
    const isMainReady = seriesMain.every((s) => s?.data?.length === expectedLength);
    const isBrushReady = seriesBrush.every((s) => s?.data?.length === expectedLength);
    if (!isMainReady || !isBrushReady) {
      return { processedSeriesMain: [], processedSeriesBrush: [], processedDateList: [] };
    }

    if (group === 'yearly') {
      // Sum by year
      const sumsByYearMain = {};
      seriesMain.forEach(({ name: operator, data }) => {
        data.forEach((val, id) => {
          const year = dateList[id].split(' ')[1];
          if (!sumsByYearMain[year]) sumsByYearMain[year] = {};
          sumsByYearMain[year][operator] = (sumsByYearMain[year][operator] || 0) + val;
        });
      });
      const sumsByYearBrush = {};
      seriesBrush[0].data.forEach((val, id) => {
        const year = dateList[id].split(' ')[1];
        sumsByYearBrush[year] = (sumsByYearBrush[year] || 0) + val;
      });
      const years = Object.keys(sumsByYearBrush);
      const processedSeriesBrush = [
        { name: seriesBrush[0].name, data: years.map((y) => sumsByYearBrush[y]) }
      ];
      const processedSeriesMain = seriesMain.map(({ name: operator }) => ({
        name: operator || '--',
        data: years.map((y) => sumsByYearMain[y][operator] || 0)
      }));

      return {
        processedSeriesBrush,
        processedSeriesMain,
        processedDateList: years
      };
    }

    // monthly: just pass originals (cloned to keep integrity)
    return {
      processedSeriesMain: seriesMain.map(({ name, data }) => ({
        name: name || '--',
        data: [...data]
      })),
      processedSeriesBrush: [{ name: seriesBrush[0].name, data: [...seriesBrush[0].data] }],
      processedDateList: [...dateList]
    };
  }, [seriesBrush, dateList, group, type]);

  const mainOptions = useMemo(() => {
    if (!processedSeriesMain?.length) return null;
    return getMainChartDefOptions(
      processedSeriesMain,
      user,
      processedDateList,
      theme,
      `${idMain}-${type}`,
      groupName,
      fileName,
      type,
      yAxisTitle
    );
  }, [processedSeriesMain, type, processedDateList]);

  const brushOptions = useMemo(() => {
    if (!processedSeriesBrush?.length) return null;
    return getBrushChartOptions(
      processedSeriesBrush,
      processedDateList,
      theme,
      `${idBrush}-${type}`,
      groupName,
      type,
      user,
      fileName
    );
  }, [processedSeriesBrush, type, processedDateList]);

  return (
    <Grid container spacing={2}>
      {!loading && (
        <Grid item xs={12}>
          <Stack direction={'row'} spacing={10} sx={{ alignItems: 'center' }}>
            <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
              <Typography variant={'body2'}>VIEW BY:</Typography>
              <ButtonGroup disableElevation>
                <Button
                  onClick={() => setType('bar')}
                  variant={type === 'bar' ? 'contained' : 'outlined'}
                  id={'bar-chart-option'}>
                  Bar Chart
                </Button>
                <Button
                  onClick={() => setType('line')}
                  variant={type === 'line' ? 'contained' : 'outlined'}
                  id={'line-chart-option'}>
                  Line Chart
                </Button>
              </ButtonGroup>
            </Stack>
            {isGrouped && (
              <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
                <Typography variant={'body2'}>GROUP BY:</Typography>
                <ButtonGroup disableElevation>
                  <Button
                    onClick={() => setGroup('monthly')}
                    variant={group === 'monthly' ? 'contained' : 'outlined'}
                    id={'bar-chart-option'}>
                    Monthly
                  </Button>
                  <Button
                    onClick={() => setGroup('yearly')}
                    variant={group === 'yearly' ? 'contained' : 'outlined'}
                    id={'line-chart-option'}>
                    Yearly
                  </Button>
                </ButtonGroup>
              </Stack>
            )}
          </Stack>
        </Grid>
      )}
      <Grid item xs={12}>
        <>
          {mainOptions && type === 'bar' && (
            <ApexChart
              loading={loading}
              height={480}
              options={mainOptions}
              type={type}
              data={processedSeriesMain || []}
            />
          )}
          {brushOptions && type === 'bar' && (
            <ApexChart
              loading={loading}
              height={130}
              options={brushOptions}
              type={type}
              data={processedSeriesBrush || []}
            />
          )}
          {mainOptions && type === 'line' && (
            <ApexChart
              loading={loading}
              height={480}
              options={mainOptions}
              type={type}
              data={processedSeriesMain || []}
            />
          )}
          {brushOptions && type === 'line' && (
            <ApexChart
              loading={loading}
              height={130}
              options={brushOptions}
              type={type}
              data={processedSeriesBrush || []}
            />
          )}
        </>
        {loading && <ApexChart height={500} options={{}} type={'bar'} loading />}
      </Grid>
    </Grid>
  );
}

TimeSeriesBrushChart.propTypes = {
  idMain: PropTypes.string.isRequired,
  idBrush: PropTypes.string.isRequired,
  groupName: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  dateList: PropTypes.arrayOf(PropTypes.string),
  seriesMain: PropTypes.arrayOf(PropTypes.object),
  seriesBrush: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  yAxisTitle: PropTypes.string,
  isGrouped: PropTypes.bool
};

TimeSeriesBrushChart.defaultProps = {
  seriesMain: [],
  seriesBrush: [],
  dateList: [],
  loading: false,
  yAxisTitle: null,
  isGrouped: false
};

export default TimeSeriesBrushChart;
